/**
 * The three templates shown in the "Generate with AI" banner, fetched ONCE per tab.
 *
 * WHAT THIS REPLACES, AND WHY IT IS A DIFFERENT SHAPE
 * ---------------------------------------------------
 * `AIGeneratorCard` used to `fetch('/ai-template-capabilities')` from a mount effect: a
 * **254 KB** response carrying all 200 families, downloaded so the browser could pick three
 * of them. Three problems, all measured:
 *
 *   1. It was the LAST thing to run. The card is `lazy()`, and it statically imported
 *      `TemplateIframePreview`, whose chunk pulls `pageNavigationUtils` — 457 kB raw /
 *      123 kB gz. Static ESM imports are blocking, so the request could not START until all
 *      of that had downloaded AND evaluated. The card is 2.5 kB gz; its blocking chain was
 *      ~156 kB gz.
 *   2. It was never cached — no `Cache-Control`, no module cache — so every page load paid a
 *      fresh round trip (~0.3–1 s on production).
 *   3. It was fetched TWICE, here and again by `TemplateLibraryAIView`.
 *
 * `/ai-showcase` returns the three picks themselves — **826 bytes** measured, a 307×
 * reduction — chosen at random on the server across three distinct campaign types. The
 * 200-family endpoint still exists and is still correct; it is now only loaded when the
 * merchant actually opens the AI popup, which genuinely needs the whole capability surface.
 *
 * ⚠️ THE PICKS ARE RECORDS, NOT NAMES, AND THAT IS LOAD-BEARING.
 * The old card matched `defaultTemplateName` against the `useDefaultTemplates` catalogue by
 * exact string equality, and its loading flag was
 *   `families === null || (picks.length > 0 && showcaseRecords.length === 0)`
 * — which has no terminal state. A join that yielded nothing (a catalog rebuild renaming a
 * template, or an environment where the import had not run) left the strip on "Loading…"
 * FOREVER: no error, no timeout, no retry. The server now resolves the records, so there is
 * no join left to fail. Do not reintroduce one.
 *
 * WHY A MODULE CACHE + sessionStorage AND NOT A CONTEXT
 * ----------------------------------------------------
 * Same argument as `useDefaultTemplates.js`, which this file deliberately mirrors: the
 * module cache dedupes across mounts with no tree surgery, and dies with the JS context —
 * which in an embedded Shopify admin is every time the merchant re-opens the app. The
 * sessionStorage layer survives that, so the strip paints on the FIRST frame of a reload.
 *
 * ⚠️ UNLIKE `useDefaultTemplates`, A STORED COPY SUPPRESSES THE REQUEST ENTIRELY.
 * There is no background revalidation. That is the explicit requirement — "cache that
 * response and don't call again" — and it is safe for the same reason the TTL is: the
 * template catalogue is static, and the three picks are a *presentation* choice, not data a
 * merchant can be wrong about. A stale trio is a different three good templates.
 * Do not copy this shape onto a merchant-owned model.
 *
 * @fileoverview Shared, deduped, session-cached fetch of the three AI showcase templates
 */

import { useState, useEffect } from 'react';

/** No `/api` prefix — Gadget maps routes directly, and the prefix 404s. */
const ENDPOINT = '/ai-showcase';

/**
 * Matches `Cache-Control: private, max-age=1800` on the route.
 *
 * Kept equal on purpose: if this TTL were longer than the header's, sessionStorage would go
 * on serving a trio the browser cache had already let go of, and the two layers would
 * disagree about how old "current" is.
 */
const TTL_MS = 30 * 60 * 1000;

/**
 * The fields this file expects back. Hashed into the storage key below so that ADDING,
 * REMOVING or RENAMING one automatically invalidates every stored copy.
 *
 * A hand-maintained `v2` suffix is one forgotten bump away from deserialising a payload
 * whose shape no longer matches the readers — which would not throw, it would silently
 * render previews missing `displayType` (every notification bar drawn as a plain popup).
 * Deriving the version from the contract makes that impossible to get wrong. Same reasoning
 * as `useDefaultTemplates.js:167`.
 */
const SHAPE = 'id,name,campaign_type,payloadUrl,templateVersion,previewImageURL,displayType,position,overlayColor,industry,family,familyName';

/**
 * A 32-bit FNV-1a in base 36. Used ONLY to version the sessionStorage key.
 *
 * @param {string} str
 * @returns {string}
 */
function hashString(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    // The FNV prime (16777619) as shifts+adds, so this stays in 32-bit integer arithmetic
    // instead of overflowing into a float via `h * 16777619`.
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(36);
}

const STORAGE_PREFIX = 'ns:aiShowcase:';
const STORAGE_KEY = `${STORAGE_PREFIX}${hashString(SHAPE)}`;

/**
 * The sessionStorage copy, read at most once per page load.
 *
 * `undefined` = not looked at yet; `null` = looked, nothing usable.
 *
 * @type {{at: number, picks: Array<Object>}|null|undefined}
 */
let storageSeed;

/**
 * @returns {{at: number, picks: Array<Object>}|null} The stored trio, or null when
 *          absent, malformed, or from a previous shape.
 */
function storedShowcase() {
  if (storageSeed !== undefined) return storageSeed;
  storageSeed = null;

  // Wrapped whole: `sessionStorage` itself throws on access in Safari private mode and under
  // a blocked-cookies policy, before getItem is ever reached.
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Shape check, not a schema check — the key hash already covers schema drift. This only
      // guards a truncated or hand-edited value.
      if (parsed && Array.isArray(parsed.picks) && parsed.picks.length > 0 && parsed.picks[0]?.id) {
        storageSeed = { at: Number(parsed.at) || 0, picks: parsed.picks };
      }
    }
  } catch {
    storageSeed = null;
  }

  return storageSeed;
}

/**
 * Persist a freshly fetched trio for the next page load.
 *
 * @param {Array<Object>} picks
 * @returns {void}
 */
function storeShowcase(picks) {
  if (!Array.isArray(picks) || picks.length === 0) return;

  try {
    const store = window.sessionStorage;

    // Sweep copies written under an older shape hash. Without this, a deploy that changes
    // SHAPE leaves the previous payload sitting in the tab's quota forever, unreadable and
    // unreachable.
    for (let i = store.length - 1; i >= 0; i -= 1) {
      const key = store.key(i);
      if (key && key.startsWith(STORAGE_PREFIX) && key !== STORAGE_KEY) store.removeItem(key);
    }

    const record = { at: Date.now(), picks };
    store.setItem(STORAGE_KEY, JSON.stringify(record));
    storageSeed = record;
  } catch {
    // Quota exceeded, private mode, blocked storage. The in-memory cache is unaffected and
    // this session simply loses the cross-reload head start — never a reason to fail.
  }
}

/**
 * @type {{at: number, promise: Promise<Array<Object>>|null, data: Array<Object>|null}}
 *
 * `promise` and `data` are separate slots on purpose. `promise` is the IN-FLIGHT request and
 * exists only while one is running; `data` is the settled result and outlives it. Collapsing
 * them would mean either serving a pending promise as data or losing the dedupe window.
 */
let cache = { at: 0, promise: null, data: null };

/**
 * The trio to serve without asking the network — memory first, then the stored copy.
 *
 * Promoting the sessionStorage copy into the module cache here (rather than only in the
 * hook's initialiser) is what makes "don't call again" hold for EVERY entry point: the
 * prime, the hook's effect and `loadAiShowcase` all go through this one function.
 *
 * @returns {Array<Object>|null} The cached picks, or null when absent or expired
 */
function freshData() {
  const now = Date.now();
  if (cache.data && now - cache.at < TTL_MS) return cache.data;

  const seed = storedShowcase();
  if (seed && now - seed.at < TTL_MS) {
    cache = { at: seed.at, promise: cache.promise, data: seed.picks };
    return seed.picks;
  }

  return null;
}

/**
 * One request, with a hard timeout.
 *
 * ⚠️ The AbortController belongs to THIS function, never to a component's unmount. The
 * promise is shared, so aborting it when one subscriber unmounts would strand the others —
 * the same trap called out at `useDefaultTemplates.js:396-399`.
 *
 * The timeout exists because a bare `fetch` can hang indefinitely, and `families === null`
 * (here: `picks === null`) is the one state the card cannot render its way out of. With it,
 * every path terminates.
 *
 * @returns {Promise<Array<Object>>}
 */
async function requestShowcase() {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 10000);

  try {
    // Plain `fetch`, deliberately. It is the working pattern across web/components/AIGenerator/
    // (including /generate-campaign), and `api.fetch()` carries a header footgun documented in
    // .claude/rules/backend.md.
    const response = await fetch(ENDPOINT, { signal: controller.signal });
    if (!response.ok) throw new Error(`/ai-showcase responded ${response.status}`);
    const body = await response.json();
    return Array.isArray(body?.picks) ? body.picks : [];
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Get the trio, starting a request only if one is neither cached nor already running.
 *
 * THE DEDUPE, precisely: `cache.promise` is assigned SYNCHRONOUSLY, in the same tick the
 * first caller enters, so every later caller until it settles awaits the SAME promise
 * object. React 18 StrictMode's double-mount lands in that branch, which is why the second
 * mount costs nothing.
 *
 * ⚠️ FAILURE IS NEVER CACHED, AND THAT IS NOT AN OPTIMISATION — IT IS WHY PRIMING IS SAFE.
 * `/ai-showcase` 401s without shop context. `primeAiShowcase()` runs from a route factory
 * that sits behind the auth gate, so it should always be authenticated — but if it ever
 * raced auth and an empty result were cached, the strip would stay hidden for the whole
 * session, which is worse than the slowness this file exists to remove. So: reject, clear
 * the slot, write nothing.
 *
 * Be precise about what that buys, though. If the card mounts while a primed request is
 * still in flight it takes the `cache.promise` branch and SUBSCRIBES to that same promise,
 * so a rejection settles both and the hook's `[]`-deps effect does not re-issue it. There is
 * no retry within that page view — the strip simply hides, which is exactly what the old
 * code did on a 401, so nothing regresses. What "never cached" guarantees is narrower and
 * still the important part: the failure is not remembered, so the next page load starts
 * clean instead of inheriting a poisoned empty result for the rest of the session.
 *
 * An empty-but-successful `{ picks: [] }` is likewise not persisted. All 200 candidate
 * templates resolve in production, so an empty answer is anomalous rather than expected, and
 * caching it for 30 minutes would hide the strip on a fluke.
 *
 * @returns {Promise<Array<Object>>}
 */
function loadAiShowcase() {
  const cached = freshData();
  if (cached) return Promise.resolve(cached);

  if (cache.promise) return cache.promise;

  const inFlight = requestShowcase().then(
    (picks) => {
      // Ownership guard: only the request still holding the slot may publish, so a
      // superseded response cannot become the next load's seed.
      if (cache.promise === inFlight) {
        cache.promise = null;
        if (picks.length > 0) {
          cache = { at: Date.now(), promise: null, data: picks };
          storeShowcase(picks);
        }
      }
      return picks;
    },
    (err) => {
      if (cache.promise === inFlight) cache.promise = null;
      throw err;
    }
  );

  cache.promise = inFlight;
  return inFlight;
}

/**
 * Start the request WITHOUT subscribing to it.
 *
 * For the `lazy()` route factory in App.jsx, which runs the moment React decides to render
 * <TemplatesPage/>. Without this the request cannot start until `AIGeneratorCard`'s module
 * body executes — and that is gated on ~156 kB gz of renderer JavaScript the request does not
 * need. Priming overlaps the two. Same pattern as `primeDefaultTemplates`, `primeAppHandle`
 * and `primeFromLocation`.
 *
 * The gain here is larger than `primeDefaultTemplates`': that hook's consumer lives in the
 * route chunk itself, one hop away, so it mainly wins on in-app transitions. This one's
 * consumer is two lazy chunks deep, so it wins on a cold load too.
 *
 * ⚠️ The `.catch` goes on the DERIVED promise, never inside `loadAiShowcase`. It marks this
 * reference handled so a failed prime is not an unhandled rejection, while leaving the shared
 * `cache.promise` rejected — which is what the hook's own `.then(onOk, onErr)` needs in order
 * to still surface the error.
 *
 * ⚠️ Never `await` this in the factory. The factory's return value is the module import;
 * awaiting first would serialise exactly what this exists to parallelise.
 *
 * @returns {void}
 */
export function primeAiShowcase() {
  loadAiShowcase().catch(() => {});
}

/**
 * The three showcase templates.
 *
 * `data` is `null` only while genuinely unresolved. Every settled outcome — success, empty,
 * or error — yields an ARRAY, so the consumer always reaches a terminal state and can never
 * be pinned on a spinner.
 *
 * @returns {{data: Array<Object>|null, fetching: boolean, error: Error|null}}
 */
export function useAiShowcase() {
  const [state, setState] = useState(() => {
    // Seeded in the initialiser rather than the effect so a reload paints the strip on the
    // FIRST render instead of flashing a skeleton for one frame with data already in hand.
    const cached = freshData();
    return { data: cached, fetching: !cached, error: null };
  });

  useEffect(() => {
    const cached = freshData();
    if (cached) {
      // Identity-compared, not merged: the cached array is a stable reference, so this is a
      // no-op re-render after the first pass rather than a setState loop.
      setState((prev) => (prev.data === cached && !prev.fetching && !prev.error
        ? prev
        : { data: cached, fetching: false, error: null }));
      return undefined;
    }

    // A plain `cancelled` flag and NO once-ref. The ref-guard + cancelled-flag combination is
    // the documented StrictMode deadlock in .claude/rules/frontend.md: cleanup #1 sets
    // cancelled, setup #2 returns early at the ref, and the in-flight promise resolves into a
    // closure that swallows it — leaving the component loading forever.
    //
    // Note what this does NOT do: it never aborts the request, because the request is shared.
    let cancelled = false;
    setState((prev) => (prev.fetching && !prev.error ? prev : { ...prev, fetching: true, error: null }));

    loadAiShowcase().then(
      (picks) => {
        if (!cancelled) setState({ data: picks, fetching: false, error: null });
      },
      (err) => {
        // Settle at an ARRAY, not null. The banner hides on an empty list, which is the
        // correct terminal state for "we could not decide what to show" — infinitely better
        // than the skeleton it used to sit on.
        if (!cancelled) setState((prev) => ({ data: prev.data || [], fetching: false, error: err }));
      }
    );

    return () => { cancelled = true; };
  }, []);

  return state;
}

/**
 * Drop the cache. For tests only — module state survives between test cases otherwise.
 *
 * @returns {void}
 */
export function __resetAiShowcaseCache() {
  cache = { at: 0, promise: null, data: null };
  storageSeed = undefined;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // No storage in this environment — which is the state the caller wanted anyway.
  }
}

export default useAiShowcase;
